import { BuiltInStyles, Column, DataSet, DataType, Style } from '@sas-dvr/ltjs-commons';
import { AnalysisRole, AxisDisplay, AxisGridDisplay, BarChart, ClassificationRole, DataSetDataProvider, GlobalLegendVisibility, Graph, KeyValue, KeyValueDisplay, KeyValueKey, LayoutGlobalLegend, LayoutOverlay, LayoutRegion, Orientation, Pad, SelectionMode, } from '@sas-dvr/ltjs-gtl';
import { TileCarousel } from '@sas/nova-commons/components/TileCarousel';
import { TiledLayout } from '@sas/nova-commons/components/TiledLayout';
import { Tile } from '@sas/nova-commons/components/Tile';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { Visualization } from '@sas-dvr/nova-graph';
import { useParentSize } from '@sas/nova-commons/hooks/useParentSize';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import cx from 'classnames';
import { ObjectSearchState, } from '../../../../store/objectSearch/ObjectSearchState';
import { selectObjectSearchState } from '../../../../store/selectors';
import { useI18n } from '../../../../locale/I18nContext';
import { useRetainable } from '../../hooks/useRetainable';
import './DemographicsResults.css';
import cn from './DemographicsResults.css.json';
const createKeyValueGraph = (label, value, format, style, noDataMessage) => {
    const layout = new LayoutRegion();
    const hasValue = value !== undefined && value !== null;
    // setup key value
    const keyValue = new KeyValue();
    keyValue.setCategory(new ClassificationRole('category'));
    keyValue.setResponse(new AnalysisRole('response'));
    keyValue.setKey(KeyValueKey.RESPONSEVALUE);
    keyValue.setTip(hasValue ? [keyValue.getResponse()] : []);
    keyValue.setSpaceFill(true);
    const keyValueDisplay = KeyValueDisplay.createSTANDARD();
    keyValueDisplay === null || keyValueDisplay === void 0 ? void 0 : keyValueDisplay.setCategorylabel(false);
    keyValueDisplay === null || keyValueDisplay === void 0 ? void 0 : keyValueDisplay.setResponselabel(!hasValue);
    keyValueDisplay === null || keyValueDisplay === void 0 ? void 0 : keyValueDisplay.setResponsevalue(hasValue);
    keyValue.setDisplay(keyValueDisplay);
    layout.setRegion(keyValue);
    // setup the graph
    const graph = new Graph();
    graph.setStyle(style !== null && style !== void 0 ? style : null);
    graph.setLayout(layout);
    graph.setSelectionMode(SelectionMode.NONE);
    graph.setPad(new Pad(0));
    graph.setLayout(layout);
    // setup the data provider
    const groupColumn = new Column();
    groupColumn.setDataType(DataType.STRING);
    groupColumn.setName('category');
    groupColumn.setStrings([hasValue ? label : noDataMessage || '']);
    const respColumn = new Column();
    respColumn.setDataType(DataType.NUMBER);
    respColumn.setName('response');
    respColumn.setNumbers([hasValue ? value : NaN]);
    respColumn.setLabel(label);
    if (format) {
        respColumn.setFormatName(format);
    }
    // assign the data provider to the graph
    const dataSet = new DataSet([groupColumn, respColumn], true, true);
    graph.setDataProvider(new DataSetDataProvider(dataSet));
    return graph;
};
const createBarChart = (label, categories, responses, format, style) => {
    const graph = new Graph();
    graph.setStyle(style !== null && style !== void 0 ? style : null);
    graph.setSelectionMode(SelectionMode.NONE);
    graph.setPad(new Pad(5));
    graph.setBorder(false);
    const layout = new LayoutOverlay();
    const barChart = new BarChart();
    const categoryRole = new ClassificationRole(label);
    const responseRole = new AnalysisRole('response');
    barChart.setResponse([responseRole]);
    barChart.setCategory(categoryRole);
    barChart.setOrientation(Orientation.HORIZONTAL);
    barChart.setTip([]); // do not show tips
    barChart.setBarLabel(false);
    barChart.setSegmentLabel(true);
    const xAxisOpt = layout.getXAxisOptions();
    xAxisOpt === null || xAxisOpt === void 0 ? void 0 : xAxisOpt.setGridDisplay(AxisGridDisplay.OFF);
    xAxisOpt === null || xAxisOpt === void 0 ? void 0 : xAxisOpt.setDisplay(AxisDisplay.createNONE());
    const yAxisDisplay = AxisDisplay.createSTANDARD();
    yAxisDisplay === null || yAxisDisplay === void 0 ? void 0 : yAxisDisplay.setLabel(true);
    yAxisDisplay === null || yAxisDisplay === void 0 ? void 0 : yAxisDisplay.setLine(false);
    const yAxisOpt = layout.getYAxisOptions();
    // yAxisOpt.getTickValueAttributes().setFontSize(new Dimension(8, DimensionUnit.POINTS));
    yAxisOpt === null || yAxisOpt === void 0 ? void 0 : yAxisOpt.setGridDisplay(AxisGridDisplay.OFF);
    yAxisOpt === null || yAxisOpt === void 0 ? void 0 : yAxisOpt.setDisplay(yAxisDisplay);
    // no legend
    const globalLegend = new LayoutGlobalLegend();
    globalLegend.setVisible(GlobalLegendVisibility.OFF);
    graph.setLayoutGlobalLegend(globalLegend);
    // add the bar chart to the layout and then set the layout on the graph
    layout.setOverlays([barChart]);
    graph.setLayout(layout);
    // assign the data provider to the bar chart
    const dataSet = new DataSet(true, true);
    const categoryCol = new Column();
    const responseCol = new Column();
    // add the data to the columns and then put the columns in the dataSet
    categoryCol.setName(label);
    categoryCol.setStrings(categories);
    categoryCol.setDataType(DataType.STRING);
    dataSet.addColumn(categoryCol);
    responseCol.setName('response');
    responseCol.setNumbers(responses);
    responseCol.setDataType(DataType.NUMBER);
    if (!!(format === null || format === void 0 ? void 0 : format.length) && format[0]) {
        responseCol.setFormatName(format[0]);
    }
    dataSet.addColumn(responseCol);
    barChart.setDataProvider(new DataSetDataProvider(dataSet));
    return graph;
};
/* basic recursive search for demographic variable by name */
const findDemographicVariableByName = (categories, name) => {
    let variable = null;
    categories === null || categories === void 0 ? void 0 : categories.some((category) => {
        const varIndex = category.variables
            ? category.variables.findIndex((variable) => variable.name === name)
            : -1;
        if (category.variables && varIndex !== -1) {
            variable = category.variables[varIndex];
        }
        else if (category.categories) {
            variable = findDemographicVariableByName(category.categories, name);
        }
        return variable !== null;
    });
    return variable;
};
export const DemographicsResultsBase = (props) => {
    var _a;
    // Local states
    const [setRef, dimensions] = useParentSize();
    const localize = useI18n();
    const [retainedGraphs = [], setRetainedGraphs] = useRetainable({
        retain: (graphs) => graphs.forEach(({ graph }) => graph.retain()),
        release: (graphs) => graphs.forEach(({ graph }) => graph.release()),
    });
    useEffect(() => {
        var _a, _b;
        const demographicData = ((_a = props.area.demographics) === null || _a === void 0 ? void 0 : _a.data) || {};
        const demographicMeasures = 
        // edge-case for empty responses: use measure space holder - to trigger a no-data keyvalue graph
        Object.keys(demographicData).length === 0 ? [''] : Object.keys(demographicData);
        const style = new Style(BuiltInStyles.NONE);
        style.addAttributeBundlesDefinedInCSSString((_b = props.visualizationStyle) !== null && _b !== void 0 ? _b : null);
        const graphs = demographicMeasures.map((measure) => {
            const result = demographicData[measure];
            return {
                measure,
                graph: !result || result.length === 1
                    ? createKeyValueGraph((result === null || result === void 0 ? void 0 : result[0].category) || measure, result === null || result === void 0 ? void 0 : result[0].value, result === null || result === void 0 ? void 0 : result[0].format, style, localize({ key: 'DemographicsResultsDialog.noData.txt' }))
                    : createBarChart(measure || '', result.map((r) => r.category).reverse(), result.map((r) => r.value).reverse(), result.map((r) => r.format).reverse(), style),
            };
        });
        setRetainedGraphs(graphs);
    }, [
        (_a = props.area.demographics) === null || _a === void 0 ? void 0 : _a.data,
        props.demographicCategories,
        setRetainedGraphs,
        localize,
        props.visualizationStyle,
    ]);
    const toolbarButtonHeight = 50;
    return retainedGraphs.length > 0 ? (React.createElement(Flow, { spacing: 's', orientation: 'vertical', className: cx(cn.formContainer, props.className), ref: setRef },
        (props.type === 'carousel' || retainedGraphs.length === 1) && dimensions && (React.createElement(TileCarousel, { className: cn.formField, disableInteractiveMode: true, showPageIndicator: true, useAddZeroStateMsg: true, pageIndicatorLocation: 'below', selectionMode: 'none', tiles: retainedGraphs.map((graph) => {
                return {
                    key: graph.measure,
                    className: cn.formTile,
                    style: {
                        height: dimensions.height -
                            toolbarButtonHeight -
                            // take additional space into account for bottom tile navigation bar
                            (retainedGraphs.length > 0 ? 20 : 0),
                        width: dimensions.width -
                            (2 * 32 /* margin nav buttons */ +
                                2 * 8 /* padding nav buttons */ +
                                2 * 8) /* padding tile */,
                    },
                    thumbnail: (React.createElement(Visualization, { applyAppTheme: !props.visualizationStyle, syncAppTheme: false, model: graph.graph, style: {
                            height: dimensions.height -
                                toolbarButtonHeight -
                                // take additional space into account for bottom tile navigation bar
                                (retainedGraphs.length > 0 ? 20 : 0),
                        }, className: cn.formGraph })),
                };
            }) })),
        props.type === 'list' && retainedGraphs.length > 1 && dimensions && (React.createElement(Flow, { spacing: 's', orientation: 'vertical', className: cn.formScrollContainer, style: { height: dimensions.height - toolbarButtonHeight } }, retainedGraphs.map((graph, index) => {
            return (React.createElement("div", { key: `viz${index}`, className: cn.formTile },
                React.createElement(Visualization, { applyAppTheme: !props.visualizationStyle, syncAppTheme: false, model: graph.graph, style: { height: dimensions.height - toolbarButtonHeight }, className: cx(cn.formGraph, cn.formGraphRelative) })));
        }))),
        props.type === 'tiles' && retainedGraphs.length > 1 && dimensions && (React.createElement(TiledLayout, { length: retainedGraphs.length, height: dimensions.height - toolbarButtonHeight, renderTile: (index) => (React.createElement(Tile, { index: index, hideCheckbox: true, body: React.createElement(Visualization, { applyAppTheme: !props.visualizationStyle, syncAppTheme: false, model: retainedGraphs[index].graph, style: {
                        width: `${Math.abs(dimensions.width / 2) - 16 * 4}px`,
                        height: `${Math.max(Math.abs(dimensions.height / 2), 100)}px`,
                    } }) })) })))) : null;
};
export const DemographicsResults = connect((state, props) => {
    var _a;
    const { features, demographicCategories, visualizationStyle } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    const area = features === null || features === void 0 ? void 0 : features.find((feature, index) => {
        return feature.type === 'area' && index === props.featureIndex;
    });
    if (!area) {
        throw new Error('Area not found');
    }
    return {
        area,
        demographicCategories,
        visualizationStyle,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(DemographicsResultsBase);
